import { z } from 'zod/v3'

import type { IrrigationScheduleConfig } from '@/lib/api/irrigation-devices-api'
import { m } from '@/lib/paraglide/messages'

// Form tưới/lịch tưới chỉ kiểm tra ô bắt buộc. Quy tắc nghiệp vụ (chồng giờ,
// 1 phút nghỉ, chu kỳ…) do backend kiểm và trả lỗi gắn đúng ô.

/** Khớp CHECK của bảng irrigation_device_names (tính theo ký tự). */
export const IRRIGATION_DEVICE_NAME_MAX = 120

/** Đổi tên trạm. Để trống là hợp lệ: dùng lại tên gốc SmartLab. */
export function createIrrigationRenameSchema() {
    return z.object({
        name: z
            .string()
            .trim()
            .refine(
                (v) => Array.from(v).length <= IRRIGATION_DEVICE_NAME_MAX,
                m.irrigation_rename_too_long(),
            ),
    })
}

export type IrrigationRenameFormValues = z.infer<
    ReturnType<typeof createIrrigationRenameSchema>
>

/** Tưới ngay / hẹn giờ một lần. */
export function createIrrigationRunSchema() {
    return z
        .object({
            mode: z.enum(['now', 'once']),
            duration_minutes: z.coerce
                .number({
                    invalid_type_error: m.irrigation_run_duration_required(),
                })
                .int(m.irrigation_run_duration_required())
                .min(1, m.irrigation_run_duration_required()),
            start_at: z.string(),
        })
        .superRefine((val, ctx) => {
            if (val.mode === 'once' && !val.start_at) {
                ctx.addIssue({
                    path: ['start_at'],
                    code: z.ZodIssueCode.custom,
                    message: m.irrigation_run_start_required(),
                })
            }
        })
}

export type IrrigationRunFormValues = z.infer<
    ReturnType<typeof createIrrigationRunSchema>
>

/** Cấu hình lịch của một bộ tưới. */
export function createIrrigationScheduleSchema() {
    const minutes = (message: string, min: number) =>
        z.coerce
            .number({ invalid_type_error: message })
            .int(message)
            .min(min, message)

    return z
        .object({
            mode: z.enum(['fixed', 'interval']),
            active: z.boolean(),
            weekdays: z.array(z.number()),
            duration_minutes: z.union([z.number(), z.string()]),
            start_times: z.array(z.object({ value: z.string() })),
            window_start: z.string(),
            window_end: z.string(),
            every_minutes: z.union([z.number(), z.string()]),
        })
        .superRefine((val, ctx) => {
            const issue = (path: Array<string | number>, message: string) =>
                ctx.addIssue({ path, code: z.ZodIssueCode.custom, message })

            if (val.weekdays.length === 0) {
                issue(['weekdays'], m.irrigation_schedule_weekdays_required())
            }
            if (!minutes('', 1).safeParse(val.duration_minutes).success) {
                issue(
                    ['duration_minutes'],
                    m.irrigation_schedule_duration_required(),
                )
            }
            if (val.mode === 'fixed') {
                if (val.start_times.length === 0) {
                    issue(
                        ['start_times'],
                        m.irrigation_schedule_time_required(),
                    )
                }
                val.start_times.forEach((t, i) => {
                    if (!t.value) {
                        issue(
                            ['start_times', i, 'value'],
                            m.irrigation_schedule_time_required(),
                        )
                    }
                })
            } else {
                if (!val.window_start) {
                    issue(
                        ['window_start'],
                        m.irrigation_schedule_window_required(),
                    )
                }
                if (!val.window_end) {
                    issue(
                        ['window_end'],
                        m.irrigation_schedule_window_required(),
                    )
                }
                if (!minutes('', 2).safeParse(val.every_minutes).success) {
                    issue(
                        ['every_minutes'],
                        m.irrigation_schedule_every_required(),
                    )
                }
            }
        })
}

export type IrrigationScheduleFormValues = z.infer<
    ReturnType<typeof createIrrigationScheduleSchema>
>

/** Giá trị form → cấu hình gửi backend; null khi chưa đủ để gửi. */
export function toScheduleConfig(
    values: IrrigationScheduleFormValues,
): IrrigationScheduleConfig | null {
    const parsed = createIrrigationScheduleSchema().safeParse(values)
    if (!parsed.success) return null
    const duration = Number(values.duration_minutes)
    if (values.mode === 'fixed') {
        return {
            mode: 'fixed',
            weekdays: [...values.weekdays].sort((a, b) => a - b),
            duration_minutes: duration,
            start_times: values.start_times.map((t) => t.value),
        }
    }
    return {
        mode: 'interval',
        weekdays: [...values.weekdays].sort((a, b) => a - b),
        duration_minutes: duration,
        window_start: values.window_start,
        window_end: values.window_end,
        every_minutes: Number(values.every_minutes),
    }
}

/** Cấu hình đã lưu của một loại lịch → giá trị form (mặc định nếu chưa lưu). */
export function toScheduleFormValues(
    mode: 'fixed' | 'interval',
    config: IrrigationScheduleConfig | null | undefined,
    active: boolean,
): IrrigationScheduleFormValues {
    return {
        mode,
        active,
        weekdays: config?.weekdays ?? [1, 2, 3, 4, 5, 6, 7],
        duration_minutes: config?.duration_minutes ?? 15,
        start_times: (config?.start_times?.length
            ? config.start_times
            : ['06:00']
        ).map((value) => ({ value })),
        window_start: config?.window_start ?? '06:00',
        window_end: config?.window_end ?? '18:00',
        every_minutes: config?.every_minutes ?? 120,
    }
}
